import { useTranslation } from 'react-i18next';

import { Box, ChevronDownOutline, ChevronUpOutline, Flex, rem, StyleProps, Text } from '@bonusly/design-system';

import { Bonus } from '../../../../types/bonus';
import RelativeTime from '../RelativeTime';
import { UserAvatarGroup } from '../User';
import BonusTextSegment from './BonusTextSegment';
import { useBonusTextTokens } from './useBonusTextTokens';
import { useCollapsibleText } from './useCollapsibleText';

interface BonusCardProps extends StyleProps {
	bonus: Bonus;
	highlight?: string;
}

// Matches text such as "![](https://www.example.com/image.jpg)""
const IMAGE_TAG_REGEX = /!\[]\(([^)]+)\)/g;

export const BonusCard = ({ bonus, highlight, ...rest }: BonusCardProps) => {
	let text = bonus.reason.replaceAll(IMAGE_TAG_REGEX, '');

	const { t } = useTranslation('common');
	const tokens = useBonusTextTokens(text);
	const { ref, show, collapsed, toggle } = useCollapsibleText();

	return (
		<Flex {...rest}>
			<Box minW={rem(60)}>
				<UserAvatarGroup isPopoverDisabled variant="compact" users={bonus.receivers} />
			</Box>

			<Flex alignItems="flex-start" direction="column" flex={1} justifyContent="center" minWidth={0}>
				<Text ref={ref} color="text.softer" fontSize="lg" fontWeight={425} noOfLines={{ ...(collapsed && { base: 3, sm: 2 }) }}>
					<Text as="span" color="text.default" fontSize="lg" fontWeight="medium">
						{bonus.giver.display_name}:{' '}
					</Text>

					{tokens.map((token, i) => (
						<BonusTextSegment key={i} token={token} bonus={bonus} highlight={highlight} />
					))}
				</Text>

				<Flex alignItems="center" mt={1}>
					<RelativeTime color="text.softest" date={bonus.created_at} fontSize="sm" fontWeight={425} letterSpacing="-0.01em" lineHeight="base" />

					{show && (
						<Flex pl={2} ml={2} borderLeftWidth={1} borderLeftColor="border.default" borderLeftStyle="solid" onClick={toggle} alignItems="center" role="group">
							<Text
								as="span"
								fontSize="sm"
								lineHeight="normal"
								color="text.softer"
								mr={1}
								textUnderlineOffset={2}
								_groupHover={{ textDecoration: 'underline' }}
							>
								{collapsed ? t('read_more') : t('read_less')}
							</Text>
							{collapsed ? <ChevronDownOutline boxSize={4} /> : <ChevronUpOutline boxSize={4} />}
						</Flex>
					)}
				</Flex>
			</Flex>
		</Flex>
	);
};
